import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn } from "@/lib/utils";

// U+2212 MINUS SIGN (not a hyphen) — visually balanced against "+", matches the
// bmc-admin-frontend `AppliedDelta` sign+word+colour convention this follows in spirit.
const MINUS_SIGN = "−";

export interface TrendIndicatorProps {
  /** Current period's raw value. */
  current: number;
  /** Prior period's raw value, or null when no prior-period data exists. */
  prior: number | null;
  /** Comparison period wording used in the aria-label, e.g. "last month". */
  periodLabel?: string;
  className?: string;
}

/**
 * Locked design decision (D8, debated and revised twice during architecture review):
 *
 * - `pctChange` is a PERCENTAGE NUMBER, not a fraction — current=122, prior=100 means
 *   pctChange=22 (i.e. "+22%"), NOT 0.22. This unit has been a real source of past
 *   confusion, hence this explicit comment.
 * - Renders nothing (null) when `prior == null` or `prior === 0` (AC#16, literal
 *   condition) — absent prior-period data must never be misrepresented as "0% change"
 *   or a fabricated "+100%".
 * - No dead-band: there is no `Math.abs(pct) < X` "treat as flat" threshold. A prior
 *   implementation attempt added one and it was explicitly rejected in architecture
 *   review as having "no basis in any AC or ADR."
 * - The displayed percentage is rounded to the nearest integer first. If the ROUNDED
 *   value is exactly 0 (e.g. a real +0.3% rounds to 0), render a neutral dash ("No
 *   change") instead of an up/down arrow with a "+0%" reading, since that would
 *   visually imply movement that isn't real.
 * - Trend direction is always icon + word/sign + colour together — never colour alone
 *   (WCAG AA).
 */
export function TrendIndicator({ current, prior, periodLabel = "last month", className }: TrendIndicatorProps) {
  if (prior == null || prior === 0) return null;

  const pctChange = ((current - prior) / prior) * 100; // percentage NUMBER, e.g. 22 means "+22%"
  const rounded = Math.round(pctChange);

  if (rounded === 0) {
    return (
      <span
        aria-label={`No change from ${periodLabel}`}
        className={cn("inline-flex items-center gap-1 text-xs font-medium text-muted-foreground", className)}
      >
        <Minus className="w-3.5 h-3.5" aria-hidden="true" />
        No change
      </span>
    );
  }

  const isUp = rounded > 0;
  const Icon = isUp ? TrendingUp : TrendingDown;
  const sign = isUp ? "+" : MINUS_SIGN;

  return (
    <span
      aria-label={`${isUp ? "Up" : "Down"} ${Math.abs(rounded)} percent from ${periodLabel}`}
      className={cn(
        "inline-flex items-center gap-1 text-xs font-semibold",
        isUp ? "text-success" : "text-destructive",
        className
      )}
    >
      <Icon className="w-3.5 h-3.5" aria-hidden="true" />
      {sign}
      {Math.abs(rounded)}%
    </span>
  );
}

export default TrendIndicator;
